import { ApartmentOutlined, ReplayOutlined } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
// Imports from MUI
import { Box, Button, Container } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import Select from "@mui/material/Select";
// Imports from Components
import { Spinner } from "components/shared/Spinner";
import { formatAttributeName, processIfCustomAttributes } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
// Imports from store
import { getCompanyList, resendWelcomeEmail, refreshAdminPortalUserList } from "store/adminPanelSlice";
import type { ICompany } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { toast } from "react-toastify";
import { translationsWithFallback } from "helpers/utils";

import StyledTextField from "../../shared/StyledTextField/StyledTextField";
import { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import useAPIPagination from "hooks/useAPIPagination";
import StyledButton from "components/shared/StyledButton";
import ControlFilter from "components/shared/ControlFilter";
import LastElementTable from "components/shared/LastElementTable";

interface UserListPanelProps {}

type CognitoUser = {
    Username: string;
    Attributes: { Name: string; Value: string }[];
    UserStatus: string;
};

const getAttr = (user: CognitoUser, name: string) => user.Attributes?.find((a) => a.Name === name)?.Value ?? "";

/** Transforms Cognito user to flat row for LastElementTable */
const userToRow = (user: CognitoUser, companies: ICompany[]) => {
    const companyId = getAttr(user, "custom:companyId");
    const company = companies.find((c) => +c.id === +companyId);
    return {
        id: user.Username,
        name: getAttr(user, "name"),
        email: getAttr(user, "email"),
        companyId,
        companyName: company?.name ?? "-",
        accountType: getAttr(user, "custom:accountType"),
        userStatus: user.UserStatus,
        username: user.Username,
    };
};

const UserList = (_props: UserListPanelProps) => {
    const dispatch = useAppDispatch();
    const {
        data: adminUsers,
        count,
        isLoading,
        lastRefresh,
    } = useAppSelector((state) => state.adminPanel.adminPortalUsersPaginated);
    const { data: companies } = useAppSelector((state) => state.adminPanel.companies);
    const [chosenCompany, setChosenCompany] = useState<ICompany | null>(null);
    const [chosenAccountType, setChosenAccountType] = useState<"all" | "beneficiary" | "publisher" | "admin">("all");

    const {
        query,
        handleQuery,
        handleRequest,
        page,
        limitPagination,
        handleChangePage,
        handleChangeLimit,
        setExtraParams,
        setPage,
        setOffsetPagination,
    } = useAPIPagination({
        type: "adminPortalUsers",
    });

    // Sync filters to API
    useEffect(() => {
        const hasCompany = !!chosenCompany;
        const hasAccountType = chosenAccountType !== "all";
        if (!hasCompany && !hasAccountType) {
            setExtraParams(undefined);
        } else {
            setExtraParams({
                ...(hasCompany && { companyId: chosenCompany!.id }),
                ...(hasAccountType && { accountType: chosenAccountType }),
            });
        }
    }, [chosenCompany, chosenAccountType, setExtraParams]);

    useEffect(() => {
        dispatch(getCompanyList());
    }, [dispatch]);

    const handleSelectChange = (ev: SelectChangeEvent<number>) => {
        const value = ev.target.value;
        setChosenCompany(value ? companies?.find((c) => c?.id === value) ?? null : null);
        setPage(0);
        setOffsetPagination(0);
    };

    const handleRefresh = async () => {
        const result = await dispatch(refreshAdminPortalUserList());
        if (refreshAdminPortalUserList.fulfilled.match(result)) {
            handleRequest({ behavior: "refresh" });
        } else {
            toast.error(translationsWithFallback((result.payload as any)?.message, "ServerError.InternalServerError"));
        }
    };

    const tableData = useMemo(
        () => (adminUsers ?? []).map((user: CognitoUser) => userToRow(user, companies ?? [])),
        [adminUsers, companies],
    );

    const columns: LastElementTableColumns[] = [
        { id: "name", label: "Name", align: "left", avatar: false },
        { id: "email", label: "Email", align: "left" },
        { id: "companyName", label: "Company", align: "left" },
        {
            id: "accountType",
            label: "Account Type",
            align: "left",
            format: (value: string) => <span>{value ?? "-"}</span>,
        },
        {
            id: "userStatus",
            label: "User Status",
            align: "left",
            format: (value: string) => (
                <span
                    style={{
                        padding: "2px 8px",
                        borderRadius: "4px",
                        backgroundColor:
                            value === "CONFIRMED" ? "rgba(131, 228, 194, 0.33)" : "rgba(243, 168, 109, 0.45)",
                    }}
                >
                    {value ?? "-"}
                </span>
            ),
        },
        {
            id: "_actions",
            label: (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRefresh();
                    }}
                    disabled={isLoading}
                    title="Actualiser"
                    style={{
                        display: "flex",
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        margin: "auto",
                    }}
                >
                    <ReplayOutlined sx={{ color: "#676767", fontSize: "1.2rem" }} />
                </button>
            ) as any,
            align: "right",
            stopPropagation: true,
            format: (_: unknown, __?: number, row?: { username: string; userStatus: string }) =>
                row?.userStatus === "FORCE_CHANGE_PASSWORD" ? (
                    <StyledButton
                        buttonType="outlined"
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(resendWelcomeEmail({ username: row.username }));
                        }}
                    >
                        Resend email
                    </StyledButton>
                ) : null,
        },
    ];

    const APIData: LastElementTableAPIData = {
        page,
        count,
        limitPagination,
        handleChangePage,
        handleChangeLimit: handleChangeLimit,
        handleSort: () => {},
    };

    return (
        <>
            <div className="admin-search">
                <StyledTextField
                    placeholder="Search"
                    value={query}
                    onChange={handleQuery}
                    sx={{ width: "550px" }}
                    InputProps={{
                        endAdornment: <SearchIcon sx={{ color: "#A8A8A8" }} />,
                    }}
                />
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Select
                        id="demo-select-small"
                        value={chosenCompany?.id ?? ""}
                        onChange={handleSelectChange}
                        MenuProps={{ className: "picker-company-popover" }}
                        className="picker-company-render"
                    >
                        <MenuItem value="">Choose a company</MenuItem>
                        {companies?.map((company, index) => (
                            <MenuItem key={index} value={company.id} sx={{ justifyContent: "space-between" }}>
                                <div className="d-flex align-items-center" style={{ overflow: "hidden" }}>
                                    {company.avatarUrl ? (
                                        <img className="avatar-company" src={company.avatarUrl} alt="company-avatar" />
                                    ) : (
                                        <ApartmentOutlined className="avatar-company" />
                                    )}
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }} title={company.name}>
                                        {company.name}
                                    </span>
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 0.5 }}>
                <Box sx={{ fontSize: "12px", color: "#676767" }}>
                    {lastRefresh && `Dernière actualisation : ${new Date(lastRefresh).toLocaleString("fr-FR")}`}
                </Box>
                <ControlFilter
                    filterLabel="Type de compte"
                    filterOptions={[
                        { value: "all", label: "Tous" },
                        { value: "beneficiary", label: "Bénéficiaires" },
                        { value: "publisher", label: "Éditeurs" },
                        { value: "admin", label: "Admins" },
                    ]}
                    controlValue={chosenAccountType}
                    controlLabel={
                        chosenAccountType === "all"
                            ? "Tous"
                            : chosenAccountType === "beneficiary"
                              ? "Bénéficiaires"
                              : chosenAccountType === "publisher"
                                ? "Éditeurs"
                                : "Admins"
                    }
                    onValueChange={(value) => {
                        setPage(0);
                        setOffsetPagination(0);
                        setChosenAccountType(value as typeof chosenAccountType);
                    }}
                />
            </Box>

            <LastElementTable
                columns={columns}
                data={tableData}
                dataType="users"
                max={-1}
                isLoading={isLoading}
                APIData={APIData}
                dataCount={count}
                columnsNumber={columns.length}
                stickyFooter
            />
        </>
    );
};

export default UserList;
